import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Search, ChevronDown, Edit2, Shield, Trash2 } from 'lucide-react';

const tierColors = {
  enterprise: 'bg-purple-100 text-purple-700',
  growth: 'bg-emerald-100 text-emerald-700',
  starter: 'bg-blue-100 text-blue-700',
  free: 'bg-slate-100 text-slate-600'
};

export const UsersTab = ({
  users, totalUsers, searchQuery, setSearchQuery, tierFilter, onTierFilter, onSearch,
  onEdit, onDelete, onTransfer, currentUserRole
}) => {
  return (
    <div className="space-y-4">
      {/* Search + Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <form onSubmit={onSearch} className="flex gap-2 flex-1 max-w-lg" data-testid="admin-user-search-form">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by email or name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="admin-user-search"
            />
          </div>
          <Button type="submit" className="bg-[#0B3D91]" data-testid="admin-user-search-btn">Search</Button>
        </form>
        <div className="flex items-center gap-3">
          <div className="relative">
            <select
              value={tierFilter}
              onChange={(e) => onTierFilter(e.target.value)}
              className="appearance-none bg-white border border-slate-200 rounded-lg px-3 py-2 pr-8 text-sm text-slate-700 cursor-pointer hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#0B3D91]/20"
              data-testid="admin-tier-filter"
            >
              <option value="">All Tiers</option>
              <option value="free">Free</option>
              <option value="starter">Starter</option>
              <option value="growth">Growth</option>
              <option value="enterprise">Enterprise</option>
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>
          <span className="text-sm text-slate-500 whitespace-nowrap" data-testid="admin-user-count">
            {totalUsers} user{totalUsers !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="admin-users-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">User</th>
                <th className="text-left p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Tier</th>
                <th className="text-left p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Role</th>
                <th className="text-left p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Joined</th>
                <th className="text-right p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.length === 0 ? (
                <tr>
                  <td colSpan="5" className="p-8 text-center text-slate-400">No users found</td>
                </tr>
              ) : users.map((u) => (
                <tr key={u.user_id} className="hover:bg-slate-50/50 transition-colors" data-testid={`admin-user-row-${u.user_id}`}>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#DCEBFF] flex items-center justify-center flex-shrink-0">
                        {u.picture ? (
                          <img src={u.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          <span className="text-[#0B3D91] font-semibold text-sm">
                            {(u.name || u.email || '?').charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 truncate">{u.name || 'No name'}</p>
                        <p className="text-sm text-slate-500 truncate">{u.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${tierColors[u.subscription_tier] || tierColors.free}`}>
                      {u.subscription_tier || 'free'}
                    </span>
                    {u.subscription_tier === 'enterprise' && u.custom_credit_limit && (
                      <span className="block text-xs text-purple-500 mt-1">{u.custom_credit_limit?.toLocaleString()} credits</span>
                    )}
                  </td>
                  <td className="p-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                      u.role === 'owner' ? 'bg-purple-100 text-purple-700' :
                      u.role === 'admin' ? 'bg-amber-100 text-amber-700' :
                      u.role === 'manager' ? 'bg-blue-100 text-blue-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {u.role || 'user'}
                    </span>
                  </td>
                  <td className="p-4 text-sm text-slate-500">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => onEdit(u)}
                        className="p-2 hover:bg-blue-50 rounded-lg transition-colors group"
                        title="Edit user"
                        data-testid={`edit-user-${u.user_id}`}
                      >
                        <Edit2 className="w-4 h-4 text-slate-400 group-hover:text-blue-600" />
                      </button>
                      {currentUserRole === 'owner' && u.role !== 'owner' && (
                        <button
                          onClick={() => onTransfer(u)}
                          className="p-2 hover:bg-purple-50 rounded-lg transition-colors group"
                          title="Transfer ownership"
                          data-testid={`transfer-owner-${u.user_id}`}
                        >
                          <Shield className="w-4 h-4 text-slate-400 group-hover:text-purple-600" />
                        </button>
                      )}
                      {u.role !== 'owner' && (
                        <button
                          onClick={() => onDelete(u)}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors group"
                          title="Delete user"
                          data-testid={`delete-user-${u.user_id}`}
                        >
                          <Trash2 className="w-4 h-4 text-slate-400 group-hover:text-red-600" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
